import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import {
  ActiveSinceEnum,
  CrmQueryFiltterDto,
  PeriodEnum,
  SpendingEnum,
} from '@/services/CrmService';

type QueryFilter = Omit<CrmQueryFiltterDto, 'page' | 'limit'>;

export enum FilterBy {
  NAME = 'NAME',
  EMAIL = 'EMAIL',
  ID = 'USER_PROFILE_ID',
}

interface ProfileContextValue {
  crmId: number | null;
  viewCrm: (userId: number | null) => void;
  queryFilter: QueryFilter | null;
  search: (searchTerm: string, filterBy: FilterBy) => void;
  resetSearch: VoidFunction;
  filter: ({
    period,
    spending,
    activeSince,
  }: {
    period?: PeriodEnum;
    spending?: SpendingEnum;
    activeSince?: ActiveSinceEnum;
  }) => void;
  resetFilters: VoidFunction;
}

export const ProfilesContext = createContext({} as ProfileContextValue);

export function ProfilesProvider({ children }: { children: ReactNode }) {
  const [crmId, setCrmId] = useState<number | null>(null);
  const [queryFilter, setQueryFilter] = useState<QueryFilter | null>(null);

  const viewCrm = useCallback((userId: number | null) => {
    setCrmId(userId);
  }, []);

  const search = useCallback((searchTerm: string, filterBy: string) => {
    setQueryFilter((state) => ({
      ...state,
      name: filterBy === FilterBy.NAME ? searchTerm : undefined,
      email: filterBy === FilterBy.EMAIL ? searchTerm : undefined,
      userProfileId: filterBy === FilterBy.ID ? searchTerm : undefined,
    }));
  }, []);

  const resetSearch = useCallback(() => {
    setQueryFilter((state) => ({
      ...state,
      name: undefined,
      email: undefined,
      userProfileId: undefined,
    }));
  }, []);

  const filter = useCallback(
    ({
      period,
      spending,
      activeSince,
    }: {
      period?: PeriodEnum;
      spending?: SpendingEnum;
      activeSince?: ActiveSinceEnum;
    }) => {
      setQueryFilter((state) => ({
        ...state,
        period,
        spending,
        activeSince,
      }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setQueryFilter((state) => ({
      ...state,
      period: undefined,
      spending: undefined,
      activeSince: undefined,
    }));
  }, []);

  return (
    <ProfilesContext.Provider
      value={{
        crmId,
        viewCrm,
        queryFilter,
        search,
        resetSearch,
        filter,
        resetFilters,
      }}
    >
      {children}
    </ProfilesContext.Provider>
  );
}

export function useProfiles() {
  return useContext(ProfilesContext);
}
